import React, { useState } from 'react';
import { ZodError } from 'zod';

import { Space as SpaceBarOutlined, AlignVerticalJustifyEnd as VerticalAlignBottomOutlined, AlignVerticalJustifyCenter as VerticalAlignCenterOutlined, AlignVerticalJustifyStart as VerticalAlignTopOutlined } from "lucide-react";
import { ToggleButton } from '@mui/material';

import { EMPTY_COLUMNS } from '../../../../documents/blocks/ColumnsContainer/columnsDefaults';
import { ColumnsCount } from '../../../../documents/blocks/ColumnsContainer/ColumnsContainerLayout';
import ColumnsContainerPropsSchema, {
  ColumnsContainerProps,
} from '../../../../documents/blocks/ColumnsContainer/ColumnsContainerPropsSchema';

import BaseSidebarPanel from './helpers/BaseSidebarPanel';
import ColumnWidthsInput from './helpers/inputs/ColumnWidthsInput';
import RadioGroupInput from './helpers/inputs/RadioGroupInput';
import SliderInput from './helpers/inputs/SliderInput';
import MultiStylePropertyPanel from './helpers/style-inputs/MultiStylePropertyPanel';

type ColumnsContainerPanelProps = {
  data: ColumnsContainerProps;
  setData: (v: ColumnsContainerProps) => void;
};

function parseColumnsCount(value: string): ColumnsCount {
  const count = Number(value);
  if (count === 2 || count === 3 || count === 4) {
    return count;
  }
  return 3;
}

export default function ColumnsContainerPanel({ data, setData }: ColumnsContainerPanelProps) {
  const [, setErrors] = useState<ZodError | null>(null);
  const columnsCount = (data.props?.columnsCount ?? 3) as ColumnsCount;

  const updateData = (d: unknown) => {
    const res = ColumnsContainerPropsSchema.safeParse(d);
    if (res.success) {
      setData(res.data);
      setErrors(null);
    } else {
      setErrors(res.error);
    }
  };

  return (
    <BaseSidebarPanel title="Columns block">
      <RadioGroupInput
        label="Number of columns"
        defaultValue={String(columnsCount)}
        onChange={(v) => {
          updateData({
            ...data,
            props: {
              ...data.props,
              columnsCount: parseColumnsCount(v),
              columns: data.props?.columns ?? [...EMPTY_COLUMNS],
            },
          });
        }}
      >
        <ToggleButton value="2">2</ToggleButton>
        <ToggleButton value="3">3</ToggleButton>
        <ToggleButton value="4">4</ToggleButton>
      </RadioGroupInput>
      <ColumnWidthsInput
        columnsCount={columnsCount}
        defaultValue={data.props?.fixedWidths ?? undefined}
        onChange={(fixedWidths) => {
          updateData({ ...data, props: { ...data.props, fixedWidths } });
        }}
      />
      <SliderInput
        label="Columns gap"
        iconLabel={<SpaceBarOutlined style={{ color: 'text.secondary' }} />}
        units="px"
        step={4}
        marks
        min={0}
        max={80}
        defaultValue={data.props?.columnsGap ?? 0}
        onChange={(columnsGap) => updateData({ ...data, props: { ...data.props, columnsGap } })}
      />
      <RadioGroupInput
        label="Alignment"
        defaultValue={data.props?.contentAlignment ?? 'middle'}
        onChange={(contentAlignment) => {
          updateData({ ...data, props: { ...data.props, contentAlignment } });
        }}
      >
        <ToggleButton value="top">
          <VerticalAlignTopOutlined className="size-4" />
        </ToggleButton>
        <ToggleButton value="middle">
          <VerticalAlignCenterOutlined className="size-4" />
        </ToggleButton>
        <ToggleButton value="bottom">
          <VerticalAlignBottomOutlined className="size-4" />
        </ToggleButton>
      </RadioGroupInput>

      <MultiStylePropertyPanel
        names={['backgroundColor', 'padding']}
        value={data.style}
        onChange={(style) => updateData({ ...data, style })}
      />
    </BaseSidebarPanel>
  );
}
